import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Clock, Sparkles, MessageCircle } from "lucide-react";
import TrustBlock from "../components/TrustBlock";
import SafetyDisclaimer from "../components/SafetyDisclaimer";
import WaitlistForm from "../components/WaitlistForm";
import AnimatedChatDemo from "../components/AnimatedChatDemo";
import { HOME_SCRIPT } from "../lib/chatScripts";
import KineticHeadline from "../components/KineticHeadline";
import Reveal from "../components/Reveal";
import FAQ from "../components/FAQ";
import TopSchoolLink from "../components/TopSchoolLink";
import { brandPhotos } from "../lib/photos";
import { useWaitlistCTA } from "../lib/useWaitlistCTA";

// Splash modal only appears after 45% scroll + 15s. Lazy-load so its JS
// is fetched in idle time, not on initial render.
const UrgencyCTA = lazy(() => import("../components/UrgencyCTA"));

const HOME_FAQ = [
    {
        q: "What is TopParent?",
        a: "TopParent is a chat-based activity planner for parents of children aged 3 to 5. It helps you choose one simple home activity that fits your child, your routine, and what today feels like.",
    },
    {
        q: "How does TopParent choose the activity?",
        a: "You share a few simple things: your child's age, energy, interests, your goal, and the time you have. TopParent matches your answers to a structured home activity and adapts it for today.",
    },
    {
        q: "Is TopParent a therapy or diagnostic tool?",
        a: "No. TopParent is not a therapy service, medical tool, diagnostic tool, or replacement for professional guidance. It suggests simple parent-led activity ideas that families can adapt around comfort, ability, routine, and safety.",
    },
    {
        q: "What information do I need to share?",
        a: "For the waitlist, we only ask for your name and email. That is all we need to let you know when early access opens.",
    },
    {
        q: "When will TopParent be ready?",
        a: "TopParent is in pre-launch. We will email you when the first activity flow is ready to try.",
    },
];

const Waitlist = () => {
    const { openWaitlist } = useWaitlistCTA();
    return (
        <div data-testid="page-waitlist">
            <Suspense fallback={null}>
                <UrgencyCTA />
            </Suspense>
            {/* ============== HERO ============== */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-brand-skyBlue/25 blur-3xl" />
                    <div className="absolute top-40 -right-24 h-[380px] w-[380px] rounded-full bg-brand-softOrange/20 blur-3xl" />
                </div>

                <div className="container-tp relative pt-10 md:pt-16 pb-14 md:pb-20">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        <div className="lg:col-span-7 animate-fade-up">
                            <span className="eyebrow mb-5 animate-fade-up">Pre-launch. Early WhatsApp testing soon.</span>
                            <KineticHeadline
                                className="h1 mb-5 mt-4"
                                text="Every child learns differently. Play should adapt to them."
                                accent="Play should adapt to them."
                                caret={true}
                                delayStart={40}
                                step={55}
                            />
                            <p className="lede max-w-xl mb-4 animate-fade-up [animation-delay:650ms]">
                                TopParent helps parents turn everyday home moments into
                                simple activity ideas shaped around their child, their
                                routine, and the time they have.
                            </p>
                            <p className="max-w-xl mb-8 text-[15px] text-[#475569] leading-[1.55] animate-fade-up [animation-delay:700ms]">
                                Starting with early access through WhatsApp. No long setup.
                                No pressure. Just low-prep ideas for real family routines.
                            </p>
                            <div className="flex flex-wrap items-center gap-3 animate-fade-up [animation-delay:800ms]">
                                <button
                                    type="button"
                                    onClick={openWaitlist}
                                    data-testid="cta_waitlist"
                                    className="btn-primary"
                                >
                                    Join the waitlist
                                    <ArrowRight size={17} strokeWidth={2} />
                                </button>
                            </div>
                            <p className="mt-4 text-[13.5px] text-[#475569] animate-fade-up [animation-delay:900ms]">
                                Be first to try the activity flow when early access opens.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-5 text-[13.5px] text-[#475569] animate-fade-up [animation-delay:950ms]">
                                <div className="flex items-center gap-2">
                                    <Heart size={14} strokeWidth={1.75} className="text-brand-coral" />
                                    From the <TopSchoolLink /> team
                                </div>
                                <div className="flex items-center gap-2">
                                    <MessageCircle size={14} strokeWidth={1.75} />
                                    Built on WhatsApp
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-5 animate-fade-up [animation-delay:150ms]">
                            <AnimatedChatDemo script={HOME_SCRIPT} testId="hero-chat-demo" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ============== 10-SECOND EXPLAINER ============== */}
            <Reveal as="section" className="container-tp pt-10 md:pt-12">
                <div
                    data-testid="product-definition"
                    data-id="activity-anchor"
                    className="relative rounded-3xl bg-white border border-brand-navy/[0.08] shadow-soft p-6 sm:p-8 md:p-10 overflow-hidden"
                >
                    <div className="absolute -top-24 -right-12 h-60 w-60 rounded-full bg-brand-skyBlue/25 blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-12 h-60 w-60 rounded-full bg-brand-softOrange/20 blur-3xl pointer-events-none" />

                    <div className="relative">
                        <div className="inline-flex items-center gap-2 rounded-full bg-brand-coral/10 border border-brand-coral/25 px-3 py-1 text-[11.5px] uppercase tracking-[0.16em] font-bold text-brand-coral mb-4">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-brand-coral opacity-75 animate-ping" />
                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-coral" />
                            </span>
                            One moment, one idea
                        </div>
                        <h2 className="font-heading text-[26px] sm:text-[32px] md:text-[36px] leading-[1.12] tracking-tight font-bold text-brand-navy max-w-3xl">
                            Open WhatsApp. Share a moment.{" "}
                            <span className="text-brand-coral">Get one idea shaped around your child.</span>
                        </h2>
                        <p className="mt-4 body-prose max-w-2xl">
                            TopParent is a simple activity idea companion for parents. Not a
                            chatbot. Not a long list. Not a course. Just one practical next
                            step, shaped around your child and the moment you are in.
                        </p>

                        {/* Concrete side-by-side: You → TopParent */}
                        <div className="mt-7 md:mt-8 grid md:grid-cols-[1fr_auto_1fr] gap-3.5 sm:gap-4 items-stretch">
                            <div className="rounded-2xl bg-brand-skyBlue/[0.10] border border-brand-skyBlue/25 p-5 sm:p-6">
                                <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-brand-coral mb-3">
                                    You, on WhatsApp
                                </p>
                                <p className="font-heading text-[15.5px] sm:text-[16px] leading-[1.5] text-brand-navy italic">
                                    &ldquo;Wednesday, 15 minutes before dinner. Loads of energy.
                                    Crayons and paper nearby.&rdquo;
                                </p>
                                <p className="mt-4 text-[12.5px] text-[#475569] leading-[1.55]">
                                    A short sentence about today. No long forms.
                                </p>
                            </div>

                            <div
                                aria-hidden="true"
                                className="hidden md:flex items-center justify-center text-brand-coral font-bold text-[20px] px-2"
                            >
                                &rarr;
                            </div>

                            <div className="rounded-2xl bg-brand-coral/[0.08] border border-brand-coral/25 p-5 sm:p-6">
                                <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-brand-coral mb-3">
                                    TopParent replies
                                </p>
                                <p className="font-heading text-[17px] sm:text-[18px] font-bold text-brand-navy leading-snug">
                                    Quiet treasure hunt
                                </p>
                                <p className="text-[12.5px] text-[#475569] mt-1 mb-3">
                                    10 minutes &middot; Crayons and paper &middot; Movement + focus
                                </p>
                                <ul className="space-y-1.5 text-[13.5px] text-[#334155] leading-[1.55]">
                                    <li className="flex gap-2">
                                        <span className="text-brand-coral font-bold tabular-nums shrink-0 w-3">1.</span>
                                        <span>Draw three small clues on paper.</span>
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-brand-coral font-bold tabular-nums shrink-0 w-3">2.</span>
                                        <span>Hide them around the room.</span>
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-brand-coral font-bold tabular-nums shrink-0 w-3">3.</span>
                                        <span>Give one clue at a time and slow the pace.</span>
                                    </li>
                                </ul>
                                <p className="mt-3 text-[12px] text-[#64748B] leading-[1.5]">
                                    Adapt, pause, or stop anytime. Save it for another day.
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap gap-2.5">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-navy/[0.10] px-3 py-1.5 text-[12.5px] font-semibold text-brand-navy">
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-coral" />
                                Built on WhatsApp
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-navy/[0.10] px-3 py-1.5 text-[12.5px] font-semibold text-brand-navy">
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-coral" />
                                Powered by PLAI&trade;
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-navy/[0.10] px-3 py-1.5 text-[12.5px] font-semibold text-brand-navy">
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-coral" />
                                Parent-controlled
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-navy/[0.10] px-3 py-1.5 text-[12.5px] font-semibold text-brand-navy">
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-coral" />
                                Low-prep, real routines
                            </span>
                        </div>
                    </div>
                </div>
            </Reveal>

            {/* ============== MADE FOR REAL FAMILY LIFE ============== */}
            <Reveal as="section" className="container-tp pt-12 md:pt-16 pb-10 md:pb-14">
                <div className="max-w-2xl mb-8 md:mb-10">
                    <h2 className="h2 mb-3">Made for real family life.</h2>
                    <p className="body-prose">
                        Practical support for the small moments between everything else.
                    </p>
                </div>
                <Reveal stagger className="grid sm:grid-cols-3 gap-4 sm:gap-5">
                    <div className="card-soft p-6 sm:p-7">
                        <span className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-brand-skyBlue/20 text-brand-navy font-heading font-bold text-[14px] mb-4">
                            01
                        </span>
                        <h3 className="font-heading text-[18px] font-bold text-brand-navy mb-2 leading-snug">
                            Activity ideas that fit your child
                        </h3>
                        <p className="body-prose">
                            Get ideas shaped around your child&rsquo;s age, interests,
                            energy, mood, and what usually helps.
                        </p>
                    </div>
                    <div className="card-soft p-6 sm:p-7">
                        <span className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-brand-softOrange/25 text-brand-navy font-heading font-bold text-[14px] mb-4">
                            02
                        </span>
                        <h3 className="font-heading text-[18px] font-bold text-brand-navy mb-2 leading-snug">
                            Support for real home moments
                        </h3>
                        <p className="body-prose">
                            Find simple ideas for before dinner, quiet time, transitions,
                            weekends, or tired afternoons.
                        </p>
                    </div>
                    <div className="card-soft p-6 sm:p-7">
                        <span className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-brand-coral/15 text-brand-navy font-heading font-bold text-[14px] mb-4">
                            03
                        </span>
                        <h3 className="font-heading text-[18px] font-bold text-brand-navy mb-2 leading-snug">
                            Flexible ideas, not pressure
                        </h3>
                        <p className="body-prose">
                            Try the activity as written, make it easier, skip it, or save it
                            for later.
                        </p>
                    </div>
                </Reveal>
            </Reveal>

            {/* ============== WHY TOPPARENT IS DIFFERENT ============== */}
            <Reveal as="section" className="container-tp pb-10 md:pb-14">
                <div className="connected-section">
                    <div className="max-w-2xl mb-8 md:mb-10">
                        <h2 className="h2 mb-3">Why TopParent is different.</h2>
                        <p className="body-prose">
                            You can find activity ideas in many places. TopParent is
                            different because it is built around your child, your home
                            routine, and what you can realistically try today.
                        </p>
                    </div>
                    <Reveal stagger className="grid sm:grid-cols-3 gap-4 sm:gap-5">
                        <div className="rounded-3xl bg-white border border-brand-navy/[0.08] p-6 sm:p-7 transition-all hover:-translate-y-0.5 hover:shadow-soft">
                            <span className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-brand-skyBlue/20 mb-4">
                                <Heart size={18} strokeWidth={1.75} className="text-brand-navy" />
                            </span>
                            <h3 className="font-heading text-[18px] font-bold text-brand-navy mb-2 leading-snug">
                                Built around your child
                            </h3>
                            <p className="body-prose">
                                TopParent is designed to consider your child&rsquo;s age,
                                interests, mood, energy level, comfort, and what usually
                                helps.
                            </p>
                        </div>
                        <div className="rounded-3xl bg-white border border-brand-navy/[0.08] p-6 sm:p-7 transition-all hover:-translate-y-0.5 hover:shadow-soft">
                            <span className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-brand-softOrange/25 mb-4">
                                <Clock size={18} strokeWidth={1.75} className="text-brand-navy" />
                            </span>
                            <h3 className="font-heading text-[18px] font-bold text-brand-navy mb-2 leading-snug">
                                Made for your real routine
                            </h3>
                            <p className="body-prose">
                                It is built for everyday moments like before dinner, after
                                school, quiet time, rainy afternoons, and bedtime
                                transitions.
                            </p>
                        </div>
                        <div className="rounded-3xl bg-white border border-brand-navy/[0.08] p-6 sm:p-7 transition-all hover:-translate-y-0.5 hover:shadow-soft">
                            <span className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-brand-coral/15 mb-4">
                                <Sparkles size={18} strokeWidth={1.75} className="text-brand-navy" />
                            </span>
                            <h3 className="font-heading text-[18px] font-bold text-brand-navy mb-2 leading-snug">
                                One idea at a time
                            </h3>
                            <p className="body-prose">
                                Instead of overwhelming you with a long list, TopParent
                                suggests one low-prep activity you can try, adapt, skip, or
                                save.
                            </p>
                        </div>
                    </Reveal>
                    <p className="mt-6 text-[14px] text-[#475569] leading-[1.6] max-w-2xl">
                        You stay in control. You choose what to share, what to try, and when
                        to stop.
                    </p>
                </div>
            </Reveal>

            {/* ============== MID-PAGE MICRO CTA ============== */}
            <Reveal as="section" className="container-tp pb-10 md:pb-14">
                <div className="rounded-3xl bg-brand-navy text-white p-6 sm:p-8 relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none opacity-25">
                        <div className="absolute -top-16 -right-10 h-60 w-60 rounded-full bg-brand-skyBlue/40 blur-3xl" />
                        <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-brand-coral/45 blur-3xl" />
                    </div>
                    <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                        <div className="max-w-xl">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/70 mb-2">
                                Early access through WhatsApp
                            </p>
                            <h3 className="font-heading text-[20px] sm:text-[22px] font-bold text-white tracking-tight leading-snug">
                                Be among the first parents to try TopParent.
                            </h3>
                            <p className="mt-1.5 text-white/80 text-[14.5px] leading-[1.55]">
                                Join the waitlist. We will email you when early access opens.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={openWaitlist}
                            data-testid="cta_waitlist"
                            className="btn-primary shrink-0"
                        >
                            Join the waitlist
                            <ArrowRight size={17} strokeWidth={2} />
                        </button>
                    </div>
                </div>
            </Reveal>

            {/* ============== WHAT YOU HAVE AT HOME IS ENOUGH ============== */}
            <Reveal as="section" className="relative">
                <div className="container-tp py-12 md:py-16">
                    <div className="relative rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden ring-1 ring-brand-navy/[0.06] shadow-hover">
                        <img
                            src={brandPhotos.enoughFlatlayWarm}
                            alt="A rice bowl, wooden spoon, crayon, napkin, and paper laid out in warm daylight"
                            loading="lazy"
                            className="w-full h-[380px] sm:h-[480px] md:h-[540px] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-brand-offWhite via-brand-offWhite/85 sm:via-brand-offWhite/70 to-brand-offWhite/20 sm:to-transparent" />
                        <div className="absolute inset-0 flex items-center">
                            <div className="px-6 sm:px-12 md:px-16 max-w-xl">
                                <span className="eyebrow mb-3 sm:mb-4">A quiet belief</span>
                                <h2 className="font-heading text-[26px] sm:text-[34px] md:text-[40px] font-bold text-brand-navy leading-[1.1] tracking-tight mt-3 sm:mt-4 mb-3 sm:mb-4">
                                    What you have at home is enough.
                                </h2>
                                <p className="body-prose max-w-md">
                                    A bowl. A spoon. A crayon. A small piece of paper. TopParent
                                    is being built around the simple things already in your home,
                                    used kindly.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Reveal>

            {/* ============== CHILDREN WHO LEARN DIFFERENTLY (compact strip) ============== */}
            <Reveal as="section" className="container-tp py-10 md:py-12">
                <div
                    data-testid="nd-home-strip"
                    className="relative rounded-3xl bg-white border border-brand-navy/[0.08] shadow-soft p-6 sm:p-7 md:p-8 overflow-hidden"
                >
                    <div className="absolute -top-16 -right-10 h-44 w-44 rounded-full bg-brand-skyBlue/20 blur-3xl pointer-events-none" />
                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
                        <div className="max-w-xl">
                            <span className="eyebrow mb-2 block">For children who learn differently</span>
                            <h3 className="font-heading text-[20px] sm:text-[22px] font-bold text-brand-navy tracking-tight leading-snug mt-2">
                                Calmer, slower, more flexible activity ideas, on the days that need them.
                            </h3>
                        </div>
                        <Link
                            to="/neurodiverse-play"
                            data-testid="cta_nd_link"
                            className="inline-flex items-center gap-2 text-brand-coral border-b border-brand-coral/40 pb-1 hover:border-brand-coral transition-colors font-bold text-[14.5px] shrink-0"
                        >
                            See how TopParent adapts
                            <ArrowRight size={15} strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </Reveal>

            {/* ============== SAFETY BOUNDARY ============== */}
            <Reveal as="section" className="container-tp pb-12 md:pb-16">
                <div className="rounded-3xl bg-brand-navy text-white p-6 sm:p-9">
                    <h2 className="font-heading text-[22px] sm:text-[26px] font-bold mb-3 text-white tracking-tight leading-snug">
                        Supportive, not clinical.
                    </h2>
                    <p className="text-white/85 text-[15.5px] leading-[1.65] max-w-2xl">
                        TopParent shares simple parent-led activity ideas for everyday home
                        moments. It is not a therapy service, medical tool, diagnostic tool, or
                        replacement for professional guidance.
                    </p>
                </div>
            </Reveal>

            {/* ============== ADVISOR VOICE ============== */}
            <Reveal as="section" className="container-tp pb-12 md:pb-16">
                <div className="connected-section">
                    <div className="max-w-2xl mb-7 md:mb-9">
                        <span className="eyebrow mb-4">A voice we are listening to</span>
                        <h2 className="h2 mt-4 mb-3">
                            Listening to people who walk this road every day.
                        </h2>
                        <p className="body-prose">
                            TopParent is being shaped with parents, caregivers, and educators
                            who understand that support at home needs to feel calm, respectful,
                            and easy to try.
                        </p>
                    </div>

                    <div
                        data-testid="advisor-card"
                        className="relative rounded-3xl bg-white border border-brand-navy/[0.08] shadow-soft p-7 sm:p-9 md:p-10 overflow-hidden"
                    >
                        <div className="absolute inset-0 pointer-events-none opacity-[0.5]">
                            <div className="absolute -top-24 -right-16 h-60 w-60 rounded-full bg-brand-skyBlue/25 blur-3xl" />
                            <div className="absolute -bottom-24 -left-12 h-60 w-60 rounded-full bg-brand-coral/15 blur-3xl" />
                        </div>

                        {/* Parent Focus Group banner: shows the working connection between Sandra (Agape) and Joseph (TopSchool) */}
                        <figure className="relative mb-7 sm:mb-9 rounded-2xl overflow-hidden ring-1 ring-brand-navy/[0.06]">
                            <img
                                src={brandPhotos.sandraJoseph}
                                alt="Parent Focus Group, January 2026. Sandra Charis Tee and Joseph Telfer with parents and educators."
                                loading="lazy"
                                className="w-full h-auto"
                            />
                            <figcaption className="px-4 py-3 text-[12.5px] text-[#475569] bg-brand-offWhite/80 backdrop-blur border-t border-brand-navy/[0.06]">
                                Parent Focus Group, January 2026. Sandra (Agape) and Joseph (
                                <a
                                    href="https://topschool.ai"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-brand-coral"
                                >
                                    TopSchool
                                </a>
                                ).
                            </figcaption>
                        </figure>

                        <div className="relative grid md:grid-cols-12 gap-8 md:gap-10 items-start">
                            {/* LEFT: identity + agape lockup */}
                            <div className="md:col-span-4">
                                <p className="font-heading text-[20px] sm:text-[22px] font-bold text-brand-navy leading-snug tracking-tight">
                                    Sandra Charis Tee
                                </p>
                                <p className="mt-1 text-[14.5px] text-[#475569] leading-[1.55] max-w-[16rem]">
                                    Lead Educational Therapist. Part-time lecturer at the National
                                    Institute of Education, NTU.
                                </p>
                                <p className="mt-1 text-[13px] text-[#64748B] leading-[1.55] max-w-[16rem]">
                                    M.Ed. (Learning Support), University of Queensland.
                                </p>

                                <a
                                    href="https://www.agape-edutherapy.com/our-therapists/sandra-charis-tee"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="advisor-org-link"
                                    className="group mt-6 pt-5 border-t border-brand-navy/[0.08] flex items-center gap-3.5"
                                >
                                    {/* Agape logo */}
                                    <span
                                        aria-hidden="true"
                                        className="shrink-0 inline-flex items-center justify-center h-14 w-14 rounded-2xl border border-brand-navy/[0.10] bg-white shadow-sm overflow-hidden"
                                    >
                                        <img
                                            src="/brand-images/agape-logo.webp"
                                            alt=""
                                            className="h-12 w-12 object-contain select-none"
                                            loading="lazy"
                                        />
                                    </span>
                                    <span className="flex flex-col">
                                        <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-navy/55">
                                            From
                                        </span>
                                        <span className="font-heading text-[15.5px] font-bold text-brand-navy tracking-tight leading-snug group-hover:text-brand-coral transition-colors">
                                            Agape Educational Therapy
                                        </span>
                                        <span className="text-[12px] text-[#64748B] group-hover:text-brand-coral/80 transition-colors">
                                            agape-edutherapy.com
                                        </span>
                                    </span>
                                </a>
                            </div>

                            {/* RIGHT: pull quote + context */}
                            <div className="md:col-span-8">
                                <blockquote className="relative">
                                    <p className="font-heading text-[22px] sm:text-[26px] leading-[1.35] text-brand-navy tracking-tight font-bold">
                                        &ldquo;Relationships and trust are at the heart of what
                                        we do. Close cooperation between caregiver and child
                                        matters more than any single activity.&rdquo;
                                    </p>
                                </blockquote>
                                <p className="mt-5 body-prose">
                                    Sandra Charis Tee has spent more than twenty years working
                                    alongside children whose learning paths look different, and the
                                    parents walking with them.
                                </p>
                                <p className="mt-4 body-prose">
                                    As a Lead Educational Therapist, part-time lecturer at the
                                    National Institute of Education, and mother of two, Sandra
                                    brings both professional depth and lived parent understanding.
                                </p>
                                <p className="mt-4 body-prose">
                                    She is helping us shape TopParent so the ideas we suggest feel
                                    calm, respectful, practical, and easy to begin at home.
                                </p>
                                <p className="mt-4 text-[13px] text-[#64748B] leading-[1.6]">
                                    A thoughtful voice we listen to. Not clinical or therapy
                                    oversight.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Reveal>

            {/* ============== TOPSCHOOL BRIDGE ============== */}
            <Reveal as="section" className="container-tp pb-12 md:pb-16">
                <div className="relative rounded-3xl bg-white border border-brand-navy/[0.08] shadow-soft p-7 sm:p-9 md:p-10 overflow-hidden">
                    {/* soft brand glow */}
                    <div className="absolute inset-0 pointer-events-none opacity-[0.5]">
                        <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-brand-skyBlue/30 blur-3xl" />
                        <div className="absolute -bottom-28 -left-12 h-60 w-60 rounded-full bg-brand-coral/15 blur-3xl" />
                    </div>
                    <div className="relative grid md:grid-cols-12 gap-8 md:gap-10 items-center">
                        {/* LEFT: copy */}
                        <div className="md:col-span-7 order-2 md:order-1">
                            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-coral mb-3 inline-block">
                                From TopSchool to TopParent
                            </span>
                            <h2 className="h2 mb-3">From the <TopSchoolLink /> team.</h2>
                            <p className="body-prose max-w-2xl mb-3">
                                TopParent is created by the team behind <TopSchoolLink />. We
                                believe learning support should feel simple, human, and useful at
                                home, not only in the classroom.
                            </p>
                            <p className="text-[14px] text-[#475569] leading-[1.6]">
                                Powered by PLAI&trade;, <TopSchoolLink />&rsquo;s shared
                                intelligence layer.
                            </p>
                            <div className="mt-5">
                                <Link
                                    to="/why"
                                    data-testid="cta_why_link"
                                    className="inline-flex items-center gap-2 text-brand-coral border-b border-brand-coral/40 pb-1 hover:border-brand-coral transition-colors font-bold text-[14.5px]"
                                >
                                    Read the founder note
                                    <ArrowRight size={15} strokeWidth={2} />
                                </Link>
                            </div>
                        </div>

                        {/* RIGHT: logo panel */}
                        <div className="md:col-span-5 order-1 md:order-2 flex md:justify-end">
                            <div
                                data-testid="topschool-logo-panel"
                                className="w-full max-w-[340px] rounded-2xl border border-brand-navy/[0.08] bg-gradient-to-br from-white via-brand-offWhite to-brand-skyBlue/15 p-6 sm:p-7 shadow-sm"
                            >
                                <div className="flex items-center justify-center h-24 sm:h-28">
                                    <img
                                        src="/brand-images/topschool-logo.webp"
                                        alt="TopSchool.ai"
                                        data-testid="topschool-logo"
                                        className="h-16 sm:h-20 w-auto select-none"
                                        loading="lazy"
                                    />
                                </div>
                                <p className="mt-5 text-center text-[12.5px] text-[#475569] leading-[1.5]">
                                    A family-side product from the <TopSchoolLink /> team.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Reveal>

            {/* ============== WAITLIST FORM ============== */}
            <Reveal as="section" id="waitlist" className="container-tp pb-12 md:pb-16 scroll-mt-24">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    <div className="lg:col-span-5">
                        <h2 className="h2 mb-4">Join the TopParent waitlist.</h2>
                        <p className="body-prose mb-5">
                            Be among the first parents to try TopParent when early access opens.
                            We&rsquo;ll send launch updates and simple ideas for supporting
                            learning through everyday home moments.
                        </p>
                        <TrustBlock title="A quiet promise">
                            No spam. No public sharing of child information. We will only use your
                            details to send TopParent updates and manage early access.
                        </TrustBlock>
                        <p className="mt-5 text-[14.5px] text-[#475569] leading-relaxed max-w-md">
                            After joining, you may receive a private invite link you can share
                            with another parent who may want simple, low-prep activity ideas at
                            home.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        <div
                            data-testid="waitlist-embed-card"
                            className="relative bg-white rounded-3xl border border-brand-navy/10 shadow-soft p-6 sm:p-8 md:p-10 overflow-hidden"
                        >
                            {/* Soft brand glow */}
                            <div className="absolute inset-0 pointer-events-none opacity-[0.55]">
                                <div className="absolute -top-24 -right-16 h-60 w-60 rounded-full bg-brand-skyBlue/25 blur-3xl" />
                                <div className="absolute -bottom-24 -left-12 h-60 w-60 rounded-full bg-brand-coral/20 blur-3xl" />
                            </div>
                            <div className="relative">
                                <WaitlistForm />

                                {/* Helpful tip: spam folder check */}
                                <div className="mt-5 flex items-start gap-3 rounded-2xl bg-brand-softOrange/[0.10] border border-brand-softOrange/30 px-4 py-3">
                                    <span className="mt-0.5 shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white text-brand-coral">
                                        <MessageCircle size={14} strokeWidth={2} />
                                    </span>
                                    <p className="text-[13px] text-[#334155] leading-[1.55]">
                                        Can&rsquo;t find our confirmation email? Please check your
                                        spam or junk folder.
                                    </p>
                                </div>

                                {/* Legal: consent + privacy */}
                                <p className="mt-3 text-[12px] text-[#64748B] leading-[1.55]">
                                    By joining, you agree to receive updates from TopParent. Opt
                                    out anytime.{" "}
                                    <a
                                        href="https://topschool.ai/privacy-policy/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline hover:text-brand-coral"
                                    >
                                        Read our privacy policy
                                    </a>
                                    .
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Reveal>

            {/* ============== REFERRAL INVITE ============== */}
            <Reveal as="section" className="container-tp pb-12 md:pb-16">
                <div className="rounded-3xl bg-brand-softOrange/[0.10] border border-brand-softOrange/25 p-7 sm:p-9">
                    <h3 className="h3 mb-3">Know a friend who could use simpler ideas at home?</h3>
                    <p className="body-prose max-w-2xl">
                        Share TopParent with a friend. After joining, they can receive their
                        own private invite link and waitlist updates.
                    </p>
                    <p className="mt-3 text-[13.5px] text-[#64748B] leading-[1.6]">
                        Simple parent-to-parent sharing.
                    </p>
                </div>
            </Reveal>

            {/* ============== FAQ ============== */}
            <FAQ
                testId="home-faq"
                title="Parent questions"
                items={HOME_FAQ}
            />

            {/* ============== SAFETY ============== */}
            <section className="container-narrow pb-14">
                <SafetyDisclaimer />
            </section>
        </div>
    );
};

export default Waitlist;
